import React, { useState, useEffect } from 'react';
import SubscriptionPlansLayer from './SubscriptionPlansLayer';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const SubscriptionControlLayer = () => {
    const [activeTab, setActiveTab] = useState('users'); // 'users' or 'plans'
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const { auth } = useAuth();

    useEffect(() => {
        if (activeTab === 'users') {
            fetchSubscriptions();
        }
    }, [activeTab]);

    const fetchSubscriptions = async () => {
        setLoading(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/all-subscriptions`);
            if (response.ok) {
                const data = await response.json();
                setSubscriptions(data.subscriptions);
            }
        } catch (error) {
            console.error('Failed to fetch subscriptions', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter logic
    const [filterPlan, setFilterPlan] = useState('All Plans');

    const filteredSubscriptions = filterPlan === 'All Plans' 
        ? subscriptions 
        : subscriptions.filter(sub => sub.plan === filterPlan);



    const getPlanBadgeClass = (planName) => {
        if (!planName) return 'bg-secondary-focus text-secondary-main';
        const lowerPlan = planName.toLowerCase();
        
        if (lowerPlan.includes('premium')) return 'bg-primary-focus text-primary-main';
        if (lowerPlan.includes('basic')) return 'bg-info-focus text-info-main';
        if (lowerPlan.includes('free')) return 'bg-success-focus text-success-main';
        
        return 'bg-secondary-focus text-secondary-main';
    };

    const getBadgeStyle = (sub) => {
        if (sub.planColor) {
            return {
                backgroundColor: sub.planColor,
                color: '#fff' // Assuming custom colors are dark enough for white text, or we could add logic
            };
        }
        return {};
    };

    const getBadgeClass = (sub) => {
        if (sub.planColor) {
            return 'badge px-24 py-4 rounded-pill fw-medium text-sm text-nowrap';
        }
        return `badge ${getPlanBadgeClass(sub.plan)} px-24 py-4 rounded-pill fw-medium text-sm text-nowrap`;
    };

    if (activeTab === 'plans') {
        return (
            <div className="d-flex flex-column h-100">
                <div className="d-flex gap-2 mb-3">
                    <button 
                        className={`btn btn-sm ${activeTab === 'users' ? 'btn-primary' : 'btn-outline-primary-600'} radius-8 px-20`}
                        onClick={() => setActiveTab('users')}
                    >
                        User Subscriptions
                    </button>
                    <button 
                        className={`btn btn-sm ${activeTab === 'plans' ? 'btn-primary' : 'btn-outline-primary-600'} radius-8 px-20`}
                        onClick={() => setActiveTab('plans')}
                    >
                        Packages Control
                    </button>
                </div>
                <SubscriptionPlansLayer />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column h-100">
            <div className="d-flex gap-2 mb-3">
                <button 
                    className={`btn btn-sm ${activeTab === 'users' ? 'btn-primary' : 'btn-outline-primary-600'} radius-8 px-20`}
                    onClick={() => setActiveTab('users')}
                >
                    User Subscriptions
                </button>
                <button 
                    className={`btn btn-sm ${activeTab === 'plans' ? 'btn-primary' : 'btn-outline-primary-600'} radius-8 px-20`}
                    onClick={() => setActiveTab('plans')}
                >
                    Packages Control
                </button>
            </div>
            <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <h6 className="text-lg fw-semibold mb-0">User Subscriptions</h6>
                <div className="d-flex align-items-center flex-wrap gap-3">
                     <select 
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" 
                        value={filterPlan}
                        onChange={(e) => setFilterPlan(e.target.value)}
                    >
                        <option value="All Plans">All Plans</option>
                        <option value="Free">Free</option>
                        <option value="Basic">Basic</option>
                        <option value="Premium">Premium</option>
                    </select>
                </div>
            </div>
            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col">Venue Name</th>
                                <th scope="col">Current Plan</th>
                                <th scope="col">Subscription Status</th>
                                <th scope="col">Renewal Date</th>
                                <th scope="col">Total Revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" className="text-center p-3">Loading subscriptions...</td></tr>
                            ) : filteredSubscriptions.length === 0 ? (
                                <tr><td colSpan="5" className="text-center p-3">No subscriptions found</td></tr>
                            ) : (
                                filteredSubscriptions.map(sub => (
                                <tr key={sub.id}>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <h6 className="text-md mb-0 fw-medium">{sub.venueName}</h6>
                                                <span className="text-sm text-secondary-light fw-medium">Stripe ID: {sub.stripeId}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span 
                                            className={getBadgeClass(sub)}
                                            style={getBadgeStyle(sub)}
                                        >
                                            {sub.plan}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${sub.status === 'active' ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'} px-24 py-4 rounded-pill fw-medium text-sm`}>{sub.status ? sub.status.charAt(0).toUpperCase() + sub.status.slice(1) : 'Unknown'}</span>
                                    </td>
                                    <td>{sub.renewalDate ? new Date(sub.renewalDate).toLocaleDateString() : 'N/A'}</td>
                                    <td>AED {sub.revenue ? sub.revenue.toFixed(2) : '0.00'}</td>
                                </tr>
                            )))}
                        </tbody>
                    </table>
                </div>
            </div>
            </div>
        </div>
    );
};

export default SubscriptionControlLayer;
